/**
 * Login screen (INVENTORY §1 screen 1, §3; prototype template lines 36-66).
 *
 * Rendered by `App.tsx` with NO props and WITHOUT the header / bottom nav —
 * `headerVM().authed` is false here. The screen is a thin view over
 * `loginVM()` + the session slice; it owns no auth logic of its own. In
 * particular it never branches on `mustReset`: `doLogin()` routes those users
 * to `screen: 'reset'` itself.
 *
 * PHASE D: the CHOOSE YOUR CHARACTER grid is RETIRED (DESIGN-CHANGELOG
 * 2026-08-18). Auth is account-level now, so one form serves every crew and
 * the picker — which could only ever list one crew's roster — had nothing left
 * to pick from. The identity field takes an email OR a player name; `loginEmail`
 * tells them apart (D3), so an invited player's link keeps working unchanged.
 *
 * THE PLACEHOLDER SAYS `EMAIL`, which is the mirrors' own label and, since
 * 2026-08-21, true. It read `EMAIL OR PLAYER NAME` for three days as a
 * deliberate, temporary deviation: the mirror's label described the design's
 * END STATE, and back then every account that predated the wizard signed in as
 * the synthesized `<user_key>@<domain>`, so typing your real address got a
 * refusal from Auth, which had never heard of it. Marcus hit exactly that with
 * his own Gmail.
 *
 * WHAT MADE IT TRUE: real login addresses were seeded onto the accounts that
 * predated email — by `scripts/seed-login-emails.mjs`, a one-off tool run once
 * per environment and DELETED 2026-08-22 once production was seeded, as its
 * own header demanded (`git log` has it) — and every account created since is
 * invited BY address. `docs/ACCOUNT-IDENTITY.md` is the lane; this line was
 * its last item.
 *
 * A PLAYER NAME STILL WORKS, and the box no longer advertises it. `loginEmail`
 * keeps appending the domain to anything without an `@`, which is the only way
 * an account that was never seeded can sign in at all — two of them still exist
 * on staging. Removing that would lock them out to tidy a placeholder.
 *
 * DEVIATIONS
 *  - D-01: seeded passwords are `START123` (8-char floor), so the prototype's
 *    dashed footnote advertises `START123`, not `START`.
 *  - The footnote renders only in a DEV build against `LocalRepository`
 *    (`showDevLogins()`); in Supabase mode there is no shared password to
 *    advertise and the box never mounts.
 */

import type { CSSProperties } from 'react';

import { PixelButton } from '../components/PixelButton.tsx';
import { QuarterIcon } from '../components/QuarterIcon.tsx';
import { useQd } from '../store/index.ts';
import { showDevLogins } from '../store/selectors.3b.ts';
import { loginVM } from '../store/selectors.ts';

const FIELD: CSSProperties = {
  flex: 1,
  minWidth: 0,
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '13px',
  padding: '12px',
  outline: 'none',
};

export default function Login(): JSX.Element {
  const s = useQd();
  const v = loginVM(s);

  return (
    <div
      data-screen-label="Login"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 14px',
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
          background: 'var(--gt-card)',
          border: '1px solid var(--gt-border)',
          boxShadow: 'var(--gt-shadow-panel)',
          padding: '28px 20px 22px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <QuarterIcon u={2} hover={false} />
          <div
            style={{
              fontFamily: 'var(--gt-font-pixel)',
              fontSize: '13px',
              color: 'var(--gt-text)',
              textAlign: 'center',
              lineHeight: 1.6,
            }}
          >
            GAMES TONIGHT<span style={{ color: 'var(--acc2)' }}>?</span>
          </div>
          <div
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: '10px',
              letterSpacing: '.14em',
              color: 'var(--gt-muted)',
            }}
          >
            WHAT ARE WE PLAYING?
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* ONE FIELD FOR TWO IDENTITIES (D3). Invited players type the name
              on their invite; owners created through the wizard type the email
              they chose. `loginEmail` decides by whether it contains an `@`,
              so the form does not have to ask which kind of account you are. */}
          <input
            value={s.sel}
            onChange={(e) => s.setSel(e.target.value)}
            onKeyDown={(e) => {
              // `e.repeat` filters a HELD Enter, which auto-repeats keydown.
              // `doLogin` drops the extras anyway; this stops them being made.
              if (e.key === 'Enter' && !e.repeat) void s.doLogin();
            }}
            placeholder="EMAIL"
            style={FIELD}
          />
          <input
            type="password"
            value={v.loginPw}
            onChange={(e) => s.setLoginPw(e.target.value)}
            onKeyDown={(e) => {
              // `e.repeat` filters a HELD Enter, which auto-repeats keydown.
              // `doLogin` drops the extras anyway; this stops them being made.
              if (e.key === 'Enter' && !e.repeat) void s.doLogin();
            }}
            placeholder="PASSWORD"
            style={FIELD}
          />
          {v.hasLoginErr ? (
            <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '11px', color: 'var(--acc2)' }}>
              ✕ <span>{v.loginErr}</span>
            </div>
          ) : null}
          <PixelButton
            label={v.loginLbl}
            busy={v.loginBusy}
            onClick={() => void s.doLogin()}
            fs={9}
            pad="15px 14px"
            center
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: '9px',
              letterSpacing: '.04em',
            }}
          >
            {/* FORGOT PASSWORD? is REAL now (D-08, docs/EMAIL.md). It said
                "not wired yet" for a week and that was the honest answer while
                every account's login address was synthesized at a domain with
                no MX: the mail had nowhere to go. SMTP, the Site URL and real
                addresses closed that, and RESET PW — the only other route back
                into a locked-out account — was removed on 2026-08-21, which is
                what turned this from a nicety into the only door. */}
            <span
              onClick={s.openForgot}
              style={{ color: 'var(--acc)', cursor: 'pointer', padding: '6px 0' }}
            >
              FORGOT PASSWORD?
            </span>
            <div style={{ flex: 1 }} />
            <span
              onClick={s.openWizard}
              style={{ color: 'var(--acc)', cursor: 'pointer', padding: '6px 0' }}
            >
              START A PARTY
            </span>
          </div>
        </div>

        {showDevLogins() ? (
          <div
            style={{
              border: '1px dashed var(--gt-border-2)',
              padding: '10px 12px',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: '9.5px',
              lineHeight: 1.8,
              color: 'var(--gt-muted)',
            }}
          >
            PROTOTYPE LOGINS — password for everyone: <span style={{ color: 'var(--gt-text-3)' }}>START123</span>
            <br />
            CHELSEA has a temp password · MARCUS is the admin
          </div>
        ) : null}
      </div>
    </div>
  );
}
